import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = '/api';

const WorkAreaContext = createContext({
  workAreas: [],
  workAreaNames: [],
  workAreaColors: {},
  workAreaMap: {},
  workAreaName: (v) => v || '',
  loading: false,
  refresh: () => {},
});

export const useWorkAreas = () => useContext(WorkAreaContext);

export const WorkAreaProvider = ({ children }) => {
  const [workAreas, setWorkAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWorkAreas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/org/work-areas`, { credentials: 'include' });
      if (res.ok) {
        setWorkAreas(await res.json());
      }
    } catch {
      // Silently fail — user may not have org yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkAreas();
  }, [fetchWorkAreas]);

  // Derive color map from work areas (keyed by name AND by work_area_id so
  // callers can pass either — the canonical reference is the id, but legacy
  // rows / new tasks sometimes pass the name.)
  const workAreaColors = React.useMemo(() => {
    const map = { '': '#E5E7EB' };
    workAreas.forEach(wa => {
      map[wa.name] = wa.color || '#E5E7EB';
      map[wa.work_area_id] = wa.color || '#E5E7EB';
    });
    return map;
  }, [workAreas]);

  // Derive name list
  const workAreaNames = React.useMemo(() => workAreas.map(wa => wa.name), [workAreas]);

  // Derive id→name map
  const workAreaMap = React.useMemo(() => {
    const map = {};
    workAreas.forEach(wa => {
      map[wa.work_area_id] = wa;
      map[wa.name] = wa;
    });
    return map;
  }, [workAreas]);

  // Resolve an id OR name to its display name. Falls back to the raw input
  // when not found (handles tasks created before a work_area was renamed /
  // deleted, legacy rows, etc.) so the UI never shows `undefined`.
  const workAreaName = React.useCallback((idOrName) => {
    if (!idOrName) return '';
    const wa = workAreaMap[idOrName];
    return wa ? wa.name : idOrName;
  }, [workAreaMap]);

  return (
    <WorkAreaContext.Provider value={{
      workAreas,
      workAreaNames,
      workAreaColors,
      workAreaMap,
      workAreaName,
      loading,
      refresh: fetchWorkAreas,
    }}>
      {children}
    </WorkAreaContext.Provider>
  );
};

export default WorkAreaContext;
